// Settings → Wizard appointments.
//
// Controls which appointment types the lead-gen wizard offers customers.
// Backed by:
//   GET /api/portal/settings/wizard-appointment-types → { types: {...} }
//   PUT /api/portal/settings/wizard-appointment-types   save
//
// Default is all-on. Retailers who don't do certain visit types (e.g. design +
// supply only) can switch them off here; the wizard hides them immediately.

import { iconMarkup } from "@tkos/shared-ui";

import type { PageContext } from "../../../shell/types";
import type { SettingsTab, TabCleanup } from "../types";

import "./wizard-appointments.css";

// Keys must match the wizard's booking-type ids + the backend column.
const APPOINTMENT_OPTIONS: Array<{ key: string; label: string; hint: string }> = [
  { key: "showroom",    label: "Showroom visit",              hint: "The customer visits your showroom." },
  { key: "home-visit",  label: "Home design visit",           hint: "Your designer visits the customer's home." },
  { key: "callback",    label: "Request a call",              hint: "No visit — you ring the customer to talk it through." },
];

interface ApptResponse {
  ok?: boolean;
  types?: Record<string, boolean>;
}

const wizardAppointmentsTab: SettingsTab = {
  id: "wizard-appointments",
  label: "Wizard Appointments",
  icon: iconMarkup("calendar", { size: "sm" }),
  mount(container: HTMLElement, ctx: PageContext): TabCleanup {
    const ac = new AbortController();
    let saving = false;
    let saveTimer: number | null = null;

    const root = document.createElement("div");
    root.className = "set-appt-root";
    root.innerHTML = `
      <p class="set-appt-intro">
        Choose which appointment types your customers can request in the lead-gen wizard.
        Turn off anything you don't offer — for example, if you're design and supply only you
        might switch off showroom and home visits. At least one type is recommended; the
        wizard hides the appointment step entirely if you turn them all off.
      </p>
      <div class="set-appt-card" data-role="options">
        ${APPOINTMENT_OPTIONS.map((o) => `
          <label class="set-appt-toggle">
            <input type="checkbox" data-key="${o.key}" />
            <span class="set-appt-toggle__text">
              <span class="set-appt-toggle__label">${o.label}</span>
              <span class="set-appt-toggle__hint">${o.hint}</span>
            </span>
          </label>
        `).join("")}
        <div class="set-appt-footer">
          <span class="set-appt-status" data-role="status"></span>
          <button type="button" class="set-btn set-btn--primary" data-role="save">Save</button>
        </div>
      </div>`;
    container.appendChild(root);

    const boxes = Array.from(root.querySelectorAll<HTMLInputElement>("input[data-key]"));
    const statusEl = root.querySelector<HTMLElement>("[data-role='status']")!;
    const saveBtn = root.querySelector<HTMLButtonElement>("[data-role='save']")!;

    const setStatus = (msg: string, kind: "" | "saved" | "error" = ""): void => {
      statusEl.textContent = msg;
      statusEl.className = `set-appt-status${kind ? ` set-appt-status--${kind}` : ""}`;
    };

    const collect = (): Record<string, boolean> => {
      const out: Record<string, boolean> = {};
      for (const b of boxes) out[b.dataset["key"] as string] = b.checked;
      return out;
    };

    const load = async (): Promise<void> => {
      setStatus("Loading…");
      try {
        const data = await ctx.api<ApptResponse>("/portal/settings/wizard-appointment-types", { signal: ac.signal });
        if (ac.signal.aborted) return;
        const types = data.types ?? {};
        for (const b of boxes) {
          const key = b.dataset["key"] as string;
          // Default to on when the key is absent (matches the all-on default).
          b.checked = types[key] !== false;
        }
        setStatus("");
      } catch (err) {
        if ((err as { name?: string }).name === "AbortError") return;
        setStatus("Couldn't load settings.", "error");
      }
    };

    const save = async (): Promise<void> => {
      if (saving) return;
      saving = true;
      setStatus("Saving…");
      try {
        await ctx.api("/portal/settings/wizard-appointment-types", {
          method: "PUT",
          body: JSON.stringify({ types: collect() }),
          signal: ac.signal,
        });
        setStatus("Saved.", "saved");
      } catch (err) {
        if ((err as { name?: string }).name === "AbortError") return;
        setStatus(err instanceof Error ? err.message : "Save failed.", "error");
      } finally {
        saving = false;
      }
    };

    saveBtn.addEventListener("click", () => void save());
    // Auto-save toggles (debounced) for a snappy feel.
    for (const b of boxes) {
      b.addEventListener("change", () => {
        if (saveTimer !== null) window.clearTimeout(saveTimer);
        saveTimer = window.setTimeout(() => { saveTimer = null; void save(); }, 300);
      });
    }

    void load();

    return () => {
      ac.abort();
      if (saveTimer !== null) window.clearTimeout(saveTimer);
      root.remove();
    };
  },
};

export default wizardAppointmentsTab;
